"use client";

import type { CareSeekerShallowLeadFormDataV2 } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { HeroVariant } from "../shared/hero-variants";

import Service from "@homeinstead/icons/Service";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { FormError } from "@homeinstead/ui/components/FormError";
import { Input } from "@homeinstead/ui/components/Input";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import ContinueButton from "../shared/ContinueButton";
import { getHeroVariantStyles } from "../shared/hero-variants";
import { Testimony } from "../Testimony";

export const NameStepV5 = ({
  nextStep,
  className,
  variant = "default",
}: {
  nextStep: () => void;
  className?: string;
  variant?: HeroVariant;
}) => {
  const [attemptedContinue, setAttemptedContinue] = useState(false);
  const { register, trigger, formState } =
    useFormContext<CareSeekerShallowLeadFormDataV2>();
  const { errors, touchedFields } = formState;

  const showError = (field: "firstName" | "lastName") =>
    (touchedFields?.[field] || attemptedContinue) && errors?.[field];

  const handleNextStep = async () => {
    setAttemptedContinue(true);
    const isValid = await trigger(["firstName", "lastName"]);
    if (isValid) {
      nextStep();
    }
  };

  const { textColorClass, errorColorClass } = getHeroVariantStyles(variant);

  const trackingEvent = {
    eventName: EventName.TRACKED_CLICK,
    eventPayloadGenerator: () => ({
      event_type: "name_step_next",
    }),
  };

  const nameLeadFormStep = {
    formVariant: "v5" as const,
    stepName: "name",
  };

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col gap-4 xl:flex-row xl:gap-y-4",
        className,
      )}
    >
      <div className="flex flex-col gap-4 md:w-[440px]">
        <h1
          className={cn(
            "mb-2 text-left font-bold font-jakarta md:mb-0",
            "text-xl md:text-1xl",
            textColorClass,
          )}
        >
          Care starts with you.
        </h1>
        <p className={cn("font-bold text-base xl:mb-2", textColorClass)}>
          Share your name so we can move forward with providing more
          information.
        </p>

        <div className="mb-4 flex flex-col gap-6 md:mb-2">
          <div>
            <Input
              placeholder="First Name"
              type="text"
              {...register("firstName")}
              className="bg-white text-base text-new-beginnings-base"
            />
            {showError("firstName") && (
              <FormError.span className={errorColorClass}>
                {errors?.firstName?.message}
              </FormError.span>
            )}
          </div>

          <div>
            <Input
              placeholder="Last Name"
              type="text"
              {...register("lastName")}
              className="bg-white text-base text-new-beginnings-base"
            />
            {showError("lastName") && (
              <FormError.span className={errorColorClass}>
                {errors?.lastName?.message}
              </FormError.span>
            )}
          </div>
        </div>

        <ContinueButton
          isEnabled={true}
          leadFormStep={nameLeadFormStep}
          nextStep={handleNextStep}
          trackingEvent={trackingEvent}
        />
      </div>
      <Testimony
        className="mb-auto ml-auto hidden max-w-md flex-col items-end justify-end gap-4 xl:flex"
        customIcon={<Service />}
        variant={variant}
      >
        <div className="flex flex-col gap-2 text-end md:max-w-[400px]">
          <div className="font-bold">We&apos;re here to help</div>
          <div>
            We&apos;re standing by and ready to provide support often with home
            care starting in 48 hours.
          </div>
        </div>
      </Testimony>
    </div>
  );
};
